import type { FieldMapping } from "../services/templates.service";

/**
 * Build the field mappings for a newly picked email template.
 *
 * Every merge field starts as an empty "Custom value" mapping, EXCEPT a field
 * that was already mapped to the "Landing page link" source under the same
 * variable name — that mapping is carried over unchanged.
 *
 * Why (2026-10-14): the normal way to build the next email in a series is to
 * duplicate a flight and pick the next template. Picking a template used to
 * reset every mapping to "Custom value", so the {{landing_page_link}} source
 * silently reverted, the Landing page link config panel disappeared, and on
 * save `metadata.landing_page_config` was dropped (the form only writes it when
 * a landing_page mapping exists). The landing-page source is flight-level
 * configuration (base URL, UTMs, custom params live on the flight, not on the
 * template), so it is safe to keep when the new template has the same variable.
 *
 * Only `landing_page` mappings are carried. Other sources (custom text such as
 * subject/preheader copy, contact, voucher, lookup, content, url) are template-
 * specific content and still reset, exactly as before.
 *
 * Matching is by exact variable name (Handlebars variables are case-sensitive).
 * A carried mapping is only kept when the new template actually uses that
 * variable, so a template without {{landing_page_link}} still ends up with no
 * landing_page mapping (and therefore no landing_page_config on save).
 */
export function initialMappingsForTemplate(
  mergeFields: ReadonlyArray<string>,
  previous: ReadonlyArray<FieldMapping>,
): FieldMapping[] {
  const carried = new Map<string, FieldMapping>();
  for (const m of previous) {
    if (m?.sourceType === "landing_page" && !carried.has(m.templateField)) {
      carried.set(m.templateField, m);
    }
  }
  return mergeFields.map((field) => {
    const keep = carried.get(field);
    if (keep) {
      return { ...keep, templateField: field };
    }
    return { templateField: field, sourceType: "custom", customValue: "" };
  });
}
